import React, { useState } from 'react';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import { useAppContext } from '../../../context/AppContext';
import { Button } from '../../../components/ui/Button';
import SafeIcon from '../../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

export default function ManageDevices() {
  const { allDevices, preloadDevice, allUsers, deleteDeviceMaster } = useAppContext();
  const [showAdd, setShowAdd] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({ serial: '', type: 'card', name: 'TapBadge Pro' });
  const [loading, setLoading] = useState(false);
  
  // NFC States
  const [nfcStatus, setNfcStatus] = useState({ id: null, state: 'idle', message: '' });

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 6; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  };

  const handleToggleAdd = () => {
    if (!showAdd) setFormData({ serial: generateRandomCode(), type: 'card', name: 'TapBadge Pro' });
    setShowAdd(!showAdd);
  };

  const getOwnerEmail = (userId) => {
    if (!userId) return null;
    return allUsers.find(u => u.id === userId)?.email || 'Unknown User';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await preloadDevice(formData.serial, formData.type, formData.name);
    setLoading(false);
    if (error) alert(error.message);
    else {
      setShowAdd(false);
    }
  };

  const handleWriteNFC = async (device) => {
    if (!('NDEFReader' in window)) {
      alert("Web NFC is not supported on this browser/device. Please use Chrome on Android or a compatible environment.");
      return;
    }

    setNfcStatus({ id: device.id, state: 'scanning', message: 'Approach tag...' });

    try {
      const ndef = new window.NDEFReader();
      await ndef.write({
        records: [{
          recordType: "url",
          data: `https://tapbadge.app/d/${device.serial_number}`
        }]
      });
      setNfcStatus({ id: device.id, state: 'success', message: 'Success!' });
      setTimeout(() => setNfcStatus({ id: null, state: 'idle', message: '' }), 3000);
    } catch (error) {
      console.error("NFC Error:", error);
      setNfcStatus({ id: device.id, state: 'error', message: `Error` });
      setTimeout(() => setNfcStatus({ id: null, state: 'idle', message: '' }), 4000);
    }
  };

  // Filter Logic
  const filteredDevices = allDevices.filter(dev => 
    dev.serial_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dev.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Device Master List</h1>
          <p className="text-gray-500 mt-1">Total Preloaded: {allDevices.length}</p>
        </div>
        
        <div className="flex w-full md:w-auto gap-3">
          <div className="relative flex-1 md:w-64">
            <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search serial..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium"
            />
          </div>
          <Button onClick={handleToggleAdd} variant={showAdd ? 'secondary' : 'primary'}>
            <SafeIcon name={showAdd ? "X" : "Plus"} className="mr-2" />
            {showAdd ? 'Cancel' : 'Preload'}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {showAdd && (
          <motion.form 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            onSubmit={handleSubmit} 
            className="mb-8 p-6 bg-purple-50 rounded-2xl border border-purple-100 grid grid-cols-1 md:grid-cols-4 gap-4 items-end overflow-hidden"
          >
            <div className="relative">
              <label className="block text-xs font-bold text-purple-700 mb-1 uppercase tracking-wider">6-Digit Code</label>
              <input required maxLength={6} value={formData.serial} onChange={(e) => setFormData({ ...formData, serial: e.target.value.toUpperCase() })} className="w-full px-4 py-2 border border-purple-200 rounded-xl font-mono font-bold outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold text-purple-700 mb-1 uppercase tracking-wider">Device Type</label>
              <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full px-4 py-2 border border-purple-200 rounded-xl bg-white">
                <option value="card">Card</option>
                <option value="keychain">Keychain</option>
                <option value="sticker">Sticker</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-purple-700 mb-1 uppercase tracking-wider">Batch Name</label>
              <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 border border-purple-200 rounded-xl outline-none" />
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'Adding...' : 'Add to System'}
            </Button>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Serial</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Device</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Owner</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredDevices.length > 0 ? (
                filteredDevices.map((dev) => (
                  <tr key={dev.id} className="group hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 font-mono font-bold text-indigo-600">{dev.serial_number}</td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">{dev.name}</span>
                      <span className="block text-[10px] text-gray-400 uppercase">{dev.type}</span>
                    </td>
                    <td className="px-6 py-4">
                      {dev.user_id ? (
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-900">{getOwnerEmail(dev.user_id)}</span>
                          <span className="text-[10px] text-green-600 font-bold uppercase">Linked</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Available</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2 items-center">
                        <button 
                          onClick={() => handleWriteNFC(dev)}
                          title="Program NFC Tag"
                          className={`px-3 py-1.5 rounded-xl transition-all flex items-center gap-2 text-xs font-bold ${
                            nfcStatus.id === dev.id 
                              ? (nfcStatus.state === 'success' ? 'bg-green-100 text-green-700' : 'bg-indigo-600 text-white animate-pulse')
                              : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white'
                          }`}
                        >
                          <SafeIcon name={nfcStatus.id === dev.id && nfcStatus.state === 'success' ? "Check" : "Zap"} />
                          {nfcStatus.id === dev.id ? nfcStatus.message : 'Write'}
                        </button>

                        <button 
                          onClick={() => { if (confirm('Delete from master list?')) deleteDeviceMaster(dev.id) }} 
                          className="p-2 text-gray-300 hover:text-red-600 transition-colors"
                        >
                          <SafeIcon name="Trash2" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500 italic">
                    No devices found matching "{searchQuery}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
